/*
 * @providesModule DepositSuccessModal
 */

import ConfettiView from 'ConfettiView';
import I18n, {_m} from 'INTL';
import ImageHeaderModal from 'ImageHeaderModal';
import Label from 'Label';
import Optional from 'Optional';
import React from 'react';
import {StyleSheet, View} from 'react-native';

import styles, {customModalStyles, ticketzStyles, zStyles} from './DepositSuccessModal.style';

export interface DepositSuccessModalDeposit {
  depositAmount: number;
  bonusCash: number;
  promoCash: number;
  ticketz?: number;
  points?: number;
  bankPromoPercentage?: number;
  bankPromoCash?: number;
  promoCouponCashAmount?: number;
}

type Props = {
  deposit: DepositSuccessModalDeposit;
  onDismiss: () => void;
};

const TEST_IDS = {
  MAIN_CONTAINER: 'deposit_success_modal',
  IMAGE: 'deposit_success_modal_check_icon',
  TITLE: 'deposit_success_modal_title',
  AMOUNT_LABEL: 'deposit_success_modal_amount_label',
  AMOUNT_VALUE: 'deposit_success_modal_amount_value',
  BONUS_CASH_LABEL: 'deposit_success_modal_bonus_cash_label',
  BONUS_CASH_VALUE: 'deposit_success_modal_bonus_cash_value',
  PROMO_BONUS_CASH_LABEL: 'deposit_success_modal_promo_bonus_cash_label',
  PROMO_BONUS_CASH_VALUE: 'deposit_success_modal_promo_bonus_cash_value',
  TICKETS_LABEL: 'deposit_success_modal_ticketz_label',
  TICKETS_VALUE: 'deposit_success_modal_ticketz_value',
  POINTS_LABEL: 'deposit_success_modal_points_label',
  POINTS_VALUE: 'deposit_success_modal_points_value',
  TOTAL_LABEL: 'deposit_success_modal_total_label',
  TOTAL_VALUE: 'deposit_success_modal_total_value',
  SUBTEXT: 'deposit_success_modal_subtext',
  PRIMARY_BUTTON: 'deposit_success_modal_ok',
  CHECKBOX: 'deposit_success_modal_checkbox',
  CHECKBOX_COPY: 'deposit_success_modal_checkbox_copy',
  BODY: 'deposit_success_modal_body',
};

const DepositSuccessModal = ({deposit, onDismiss}: Props) => {

  const promoCouponCashAmount = 
    deposit.promoCouponCashAmount ?? 0;

  const totalValue =
    deposit.depositAmount + deposit.bonusCash + deposit.promoCash + (deposit.bankPromoCash ?? 0) + promoCouponCashAmount;

  const _renderBodyContent = (): React.ReactElement => {
    return (
      <View>
        <View style={styles.bodyRowContainer}>
          <Label style={styles.rowText} testID={TEST_IDS.AMOUNT_LABEL}>
            {_m('Deposit Amount')}
          </Label>
          <Label style={styles.rowText} testID={TEST_IDS.AMOUNT_VALUE}>
            {I18n.getCurrencyString(deposit.depositAmount, '$', false, true)}
          </Label>
        </View>
        <Optional test={deposit.bonusCash > 0}>
          <View style={styles.bodyRowContainer}>
            <Label style={styles.rowText} testID={TEST_IDS.BONUS_CASH_LABEL}>
              {_m('Bonus Cash')}
            </Label>
            <Label style={styles.rowText} testID={TEST_IDS.BONUS_CASH_VALUE}>
              {I18n.getCurrencyString(deposit.bonusCash + promoCouponCashAmount, '$', false, true)}
            </Label>
          </View>
        </Optional>
        <Optional test={deposit.promoCash > 0}>
          <View style={styles.bodyRowContainer}>
            <Label style={styles.rowText} testID={TEST_IDS.PROMO_BONUS_CASH_LABEL}>
              {_m('Promo Bonus Cash')}
            </Label>
            <Label style={styles.rowText} testID={TEST_IDS.PROMO_BONUS_CASH_VALUE}>
              {I18n.getCurrencyString(deposit.promoCash, '$', false, true)}
            </Label>
          </View>
        </Optional>
        <Optional test={(deposit.bankPromoCash ?? 0) > 0 && (deposit.bankPromoPercentage ?? 0) > 0}>
          <View style={styles.bodyRowContainer}>
            <Label style={styles.rowText} testID={TEST_IDS.PROMO_BONUS_CASH_LABEL}>
              {_m('Bank Promo ({0}%)', deposit.bankPromoPercentage)}
            </Label>
            <Label style={styles.rowText} testID={TEST_IDS.PROMO_BONUS_CASH_VALUE}>
              {I18n.getCurrencyString(deposit.bankPromoCash!, '$', false, true)}
            </Label>
          </View>
        </Optional>
        <Optional test={(deposit.ticketz ?? 0) > 0}>
          <View style={styles.bodyRowContainer}>
            <View style={ticketzStyles.ticketLabelContainer}>
              <Label style={styles.rowText} testID={TEST_IDS.TICKETS_LABEL}>
                {_m('Ticketz', deposit.ticketz ?? 0)}
              </Label>
            </View>
            <Label style={styles.rowText} testID={TEST_IDS.TICKETS_VALUE}>
              {I18n.getIntegerString(deposit.ticketz ?? 0)}
            </Label>
          </View>
        </Optional>
        <Optional test={(deposit.points ?? 0) > 0}>
          <View style={styles.bodyRowContainer}>
            <View style={zStyles.pointsLabelContainer}>
              <Label style={styles.rowText} testID={TEST_IDS.POINTS_LABEL}>
                {_m('Z Coins')}
              </Label>
            </View>
            <Label style={styles.rowText} testID={TEST_IDS.POINTS_VALUE}>
              {I18n.getIntegerString(deposit.points ?? 0)}
            </Label>
          </View>
        </Optional>
        <View style={styles.divider} />
        <View style={[styles.bodyRowContainer, {paddingBottom: 16}]}>
          <Label style={styles.totalText} testID={TEST_IDS.TOTAL_LABEL}>
            {_m('Total')}
          </Label>
          <Label style={styles.totalText} testID={TEST_IDS.TOTAL_VALUE}>
            {I18n.getCurrencyString(totalValue, '$', false, true)}
          </Label>
        </View>
        <Label
          style={styles.disclaimerText}
          numberOfLines={0}
          adjustsFontSizeToFit={false}
          testID={TEST_IDS.SUBTEXT}
        >
          {_m('A receipt of this transaction has been sent to your email for your records')}
        </Label>
      </View>
    );
  };

  return (
    <ConfettiView
      style={[styles.confetti, StyleSheet.absoluteFill]}
      avoidFront={false}
      shouldPlay={true}
    >
      <ImageHeaderModal
        title={_m('Deposit Successful')}
        buttonTextPrimary={_m('Ok')}
        imageHeader={'icon_deposit_check.svg'}
        imageWidth={40}
        imageAspectRatio={1}
        backdropShadowEnabled={true}
        testIDs={TEST_IDS}
        onPressPrimary={onDismiss}
        customStyles={customModalStyles}
      >
        {_renderBodyContent()}
      </ImageHeaderModal>
    </ConfettiView>
  );
};

export default DepositSuccessModal;
